import { ledgerService } from '@/api/ledger.service';
import { BOOKINGS_QUERY_KEY } from '@/queries/use-bookings';
import type {
  LedgerControllerFindAllLedgerEntriesData,
  AllocatePaymentToLedgerEntriesDto,
  AllocateInvoiceToLedgerEntriesDto,
  RecalculateLedgerEntriesDto,
} from '@carvia/ros-client-types';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useQuery } from '@tanstack/vue-query';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toRef, toValue } from 'vue';
import { INVOICES_QUERY_KEY } from './use-invoices';

export const LEDGER_ENTRIES_QUERY_KEY = 'ledger-entries';
export const INVOICE_ALLOCATIONS_QUERY_KEY = 'invoice-allocations';
export const PAYMENT_ALLOCATIONS_QUERY_KEY = 'payment-allocations';
export const NETTING_ELIGIBILITY_QUERY_KEY = 'netting-eligibility';

// Keep in sync with PAYMENTS_QUERY_KEY in use-payments.ts (avoid circular import).
const PAYMENTS_QUERY_KEY = 'payments';

export const useAllocatePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: AllocatePaymentToLedgerEntriesDto) =>
      ledgerService.allocatePaymentToLedgerEntries(dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PAYMENT_ALLOCATIONS_QUERY_KEY, variables.paymentId],
      });
      void queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
    },
  });
};

export const useRecalculateLedgerEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: RecalculateLedgerEntriesDto) => ledgerService.recalculateLedgerEntries(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
    },
  });
};

export const useAllocateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: AllocateInvoiceToLedgerEntriesDto) =>
      ledgerService.allocateInvoiceToLedgerEntries(dto),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [INVOICE_ALLOCATIONS_QUERY_KEY, variables.invoiceId],
      });
      void queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
    },
  });
};

export const useLedgerEntries = (
  query: MaybeRefOrGetter<LedgerControllerFindAllLedgerEntriesData['query']>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [LEDGER_ENTRIES_QUERY_KEY, query],
    queryFn: ({ pageParam = 1 }) =>
      ledgerService.findAllLedgerEntries({ ...toValue(query), page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
    enabled,
  });
};

export const usePaymentAllocations = (
  paymentId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [PAYMENT_ALLOCATIONS_QUERY_KEY, paymentId],
    queryFn: () => ledgerService.getPaymentAllocationsByPaymentId(toValue(paymentId)!),
    enabled: () => toValue(enabled) && !!toValue(paymentId),
  });
};

export const useInvoiceAllocations = (
  invoiceId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [INVOICE_ALLOCATIONS_QUERY_KEY, invoiceId],
    queryFn: () => ledgerService.getInvoiceAllocationsByInvoiceId(toValue(invoiceId)!),
    enabled: () => toValue(enabled) && !!toValue(invoiceId),
  });
};

export const useCheckNettingEligibility = (
  bookingId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [NETTING_ELIGIBILITY_QUERY_KEY, toRef(bookingId)],
    queryFn: () => ledgerService.checkNettingEligibility(toValue(bookingId)!),
    enabled: computed(() => toValue(enabled) && !!toValue(bookingId)),
  });
};

export const useAutoNetBookingLedgerEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => ledgerService.autoNetBookingLedgerEntries(bookingId),
    onSuccess: (_, bookingId) => {
      queryClient.invalidateQueries({ queryKey: [LEDGER_ENTRIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [NETTING_ELIGIBILITY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY, bookingId] });
    },
  });
};
